/**
 * AI 组件核心 Hook（对齐 CompsAi.newDesign.md）
 * 处理：消息校验、event_hash/cache_hash、思考方法查询、缓存读写、模型调用、结果聚合。
 */
import { useCallback } from 'react';
const API_PATHS = {
    getThinkingMethods: '/api/ai/get-thinking-methods',
    getCache: '/api/ai/get-cache',
    saveCache: '/api/ai/save-cache',
    callModel: '/api/ai/call-model',
};
function ensureSlash(path) {
    return path.startsWith('/') ? path : `/${path}`;
}
/** 浏览器环境 SHA-256（与后端算法一致），兼容 crypto.subtle 不可用场景（如非 HTTPS、部分 iframe、Secure Context） */
async function sha256Hex(input) {
    const enc = new TextEncoder();
    const bytes = enc.encode(input);
    const subtle = globalThis.crypto?.subtle;
    if (subtle != null) {
        try {
            const data = await subtle.digest('SHA-256', bytes);
            return Array.from(new Uint8Array(data))
                .map((b) => b.toString(16).padStart(2, '0'))
                .join('');
        }
        catch {
            // digest 抛错（如 Secure Context 不可用、reading 'digest' of undefined）时回退
        }
    }
    return sha256HexFallback(bytes);
}
/** 纯 JS SHA-256 回退实现（当 crypto.subtle 不可用时） */
function sha256HexFallback(bytes) {
    const K = [
        0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
        0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
        0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
        0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
        0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
        0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
        0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
        0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
    ];
    const H = [0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19];
    const len = bytes.length;
    const bits = len * 8;
    const padding = ((56 - (len % 64) + 64) % 64) || 64;
    const total = len + padding;
    const buf = new ArrayBuffer(total + 8);
    const view = new DataView(buf);
    const arr = new Uint8Array(buf);
    arr.set(bytes, 0);
    arr[len] = 0x80;
    view.setUint32(total, (bits / 0x100000000) >>> 0, false);
    view.setUint32(total + 4, bits >>> 0, false);
    const W = new Uint32Array(64);
    for (let off = 0; off < total; off += 64) {
        for (let i = 0; i < 16; i++) {
            W[i] = view.getUint32(off + i * 4, false);
        }
        for (let i = 16; i < 64; i++) {
            const s0 = ((W[i - 15] >>> 7) | (W[i - 15] << 25)) ^ ((W[i - 15] >>> 18) | (W[i - 15] << 14)) ^ (W[i - 15] >>> 3);
            const s1 = ((W[i - 2] >>> 17) | (W[i - 2] << 15)) ^ ((W[i - 2] >>> 19) | (W[i - 2] << 13)) ^ (W[i - 2] >>> 10);
            W[i] = (W[i - 16] + s0 + W[i - 7] + s1) >>> 0;
        }
        let [a, b, c, d, e, f, g, h] = H;
        for (let i = 0; i < 64; i++) {
            const S1 = ((e >>> 6) | (e << 26)) ^ ((e >>> 11) | (e << 21)) ^ ((e >>> 25) | (e << 7));
            const ch = (e & f) ^ (~e & g);
            const t1 = (h + S1 + ch + K[i] + W[i]) >>> 0;
            const S0 = ((a >>> 2) | (a << 30)) ^ ((a >>> 13) | (a << 19)) ^ ((a >>> 22) | (a << 10));
            const maj = (a & b) ^ (a & c) ^ (b & c);
            const t2 = (S0 + maj) >>> 0;
            h = g;
            g = f;
            f = e;
            e = (d + t1) >>> 0;
            d = c;
            c = b;
            b = a;
            a = (t1 + t2) >>> 0;
        }
        H[0] = (H[0] + a) >>> 0;
        H[1] = (H[1] + b) >>> 0;
        H[2] = (H[2] + c) >>> 0;
        H[3] = (H[3] + d) >>> 0;
        H[4] = (H[4] + e) >>> 0;
        H[5] = (H[5] + f) >>> 0;
        H[6] = (H[6] + g) >>> 0;
        H[7] = (H[7] + h) >>> 0;
    }
    return H.map((v) => v.toString(16).padStart(8, '0')).join('');
}
/**
 * 压缩：将 JSON 对象转为 gzip 的 ArrayBuffer。
 * 若环境中无 pako，则回退为 JSON 字符串的 UTF-8 字节（未压缩）。
 */
async function compressToBuffer(obj) {
    const jsonStr = JSON.stringify(obj);
    try {
        const pako = await import('pako');
        const compressed = pako.gzip(jsonStr);
        return compressed.buffer.slice(compressed.byteOffset, compressed.byteOffset + compressed.byteLength);
    }
    catch {
        return new TextEncoder().encode(jsonStr).buffer;
    }
}
/**
 * 解压：从 ArrayBuffer/Blob 恢复为对象。
 * 若前端收到的是 gzip，需与后端约定一致；此处按「先尝试 gzip，失败则按 UTF-8 文本」处理。
 */
async function decompressFromBlob(blob) {
    const buffer = await blob.arrayBuffer();
    const bytes = new Uint8Array(buffer);
    try {
        const pako = await import('pako');
        const str = pako.ungzip(bytes, { to: 'string' });
        return JSON.parse(str);
    }
    catch {
        const str = new TextDecoder().decode(bytes);
        return JSON.parse(str);
    }
}
/**
 * 从 payload 中抽取「结构」：保留字段名与层级，将具体值替换为类型占位（null/[]/""），
 * 用于生成只与结构相关的 eventHash（与 CompsAi 中的实现保持一致）。
 */
function getPayloadStructure(val) {
    if (val === null || val === undefined)
        return null;
    if (Array.isArray(val)) {
        if (val.length === 0)
            return [];
        return [getPayloadStructure(val[0])];
    }
    if (typeof val === 'object') {
        const obj = val;
        const out = {};
        for (const k of Object.keys(obj)) {
            out[k] = getPayloadStructure(obj[k]);
        }
        return out;
    }
    if (typeof val === 'string')
        return '';
    if (typeof val === 'number')
        return null;
    if (typeof val === 'boolean')
        return null;
    return null;
}
export function useAiComponentHook(options = {}) {
    const { apiBaseUrl = '', fetchFn = fetch } = options;
    const base = (path) => {
        const p = ensureSlash(path);
        if (!apiBaseUrl)
            return p;
        const root = apiBaseUrl.replace(/\/$/, '');
        return `${root}${p}`;
    };
    const generateMsgId = useCallback(() => {
        return typeof crypto !== 'undefined' && crypto.randomUUID
            ? crypto.randomUUID()
            : `msg-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    }, []);
    const formatTriggerTime = useCallback(() => {
        return new Date().toISOString();
    }, []);
    const generateEventHash = useCallback(async (eventContent) => {
        return sha256Hex(eventContent);
    }, []);
    const generateCacheHash = useCallback(async (eventHash, methodId, dataKeySuffix) => {
        const baseStr = `${eventHash}*${methodId}`;
        const suffix = dataKeySuffix != null && String(dataKeySuffix).trim() ? String(dataKeySuffix).trim() : '';
        return sha256Hex(suffix ? `${baseStr}*${suffix}` : baseStr);
    }, []);
    const compressResult = useCallback((result) => {
        return compressToBuffer(result);
    }, []);
    const decompressResult = useCallback(async (blob) => {
        return decompressFromBlob(blob);
    }, []);
    /**
     * 处理传入消息：按流程图「先查缓存、未命中再调 AI、结果写回缓存」。
     * @param msg 标准消息
     * @param clickedMethodId 若传入（如点击某个思考方式），则只处理该 method：先整理哈希查缓存，无缓存再调 AI 并保存
     * @param forceRefresh 为 true 时跳过缓存，直接从后端获取并更新缓存表（用于「刷新」按钮）
     * @param eventHashOverride 若传入（如后端返回的 event_hash），则用其请求思考方法，避免与库中事件不一致导致「未找到思考方法」
     */
    const handleIncomingMessage = useCallback(async (msg, clickedMethodId, forceRefresh, eventHashOverride) => {
        const requiredFields = [
            'msgId',
            'source',
            'eventData',
        ];
        const missing = requiredFields.filter((f) => msg[f] == null);
        if (missing.length > 0) {
            throw new Error(`消息格式错误，缺失字段：${missing.join(', ')}`);
        }
        // 优先使用调用方传入的后端 eventHash，与库中事件一致，避免「未找到思考方法」
        let eventHash;
        if (eventHashOverride && typeof eventHashOverride === 'string') {
            eventHash = eventHashOverride;
        }
        else {
            const structuredPayload = getPayloadStructure(msg.eventData.payload);
            const eventContentStr = JSON.stringify(structuredPayload);
            eventHash = await generateEventHash(eventContentStr);
        }
        const apiUrl = `${base(API_PATHS.getThinkingMethods)}?eventHash=${encodeURIComponent(eventHash)}&pageId=${encodeURIComponent(msg.source.pageId)}`;
        console.log('📡 [useAiComponentHook] 请求思考方法:', {
            url: apiUrl,
            eventHash,
            pageId: msg.source.pageId,
            componentId: msg.source.componentId,
            clickedMethodId: clickedMethodId ?? '全部',
        });
        const thinkingMethodsRes = await fetchFn(apiUrl, { headers: { Authorization: msg.context?.token || '' } });
        if (!thinkingMethodsRes.ok) {
            const errorText = await thinkingMethodsRes.text().catch(() => thinkingMethodsRes.statusText);
            console.error('❌ [useAiComponentHook] 获取思考方法失败:', {
                status: thinkingMethodsRes.status,
                statusText: thinkingMethodsRes.statusText,
                url: apiUrl,
                errorText,
            });
            throw new Error(`获取思考方法失败: ${thinkingMethodsRes.status} ${thinkingMethodsRes.statusText}${errorText ? ` - ${errorText}` : ''}`);
        }
        const thinkingMethodsJson = await thinkingMethodsRes.json();
        console.log('✅ [useAiComponentHook] 思考方法响应:', {
            hasData: !!thinkingMethodsJson?.data,
            isArray: Array.isArray(thinkingMethodsJson?.data),
            dataLength: Array.isArray(thinkingMethodsJson?.data) ? thinkingMethodsJson.data.length : 'N/A',
        });
        let thinkingMethods = Array.isArray(thinkingMethodsJson?.data)
            ? thinkingMethodsJson.data
            : thinkingMethodsJson?.data
                ? [thinkingMethodsJson.data]
                : [];
        if (clickedMethodId) {
            thinkingMethods = thinkingMethods.filter((m) => m.method_id === clickedMethodId ||
                m.id === clickedMethodId);
            if (thinkingMethods.length === 0) {
                console.warn('⚠️ [useAiComponentHook] 未找到点击的思考方法:', {
                    clickedMethodId,
                    eventHash,
                });
                throw new Error(`未找到思考方法: ${clickedMethodId}`);
            }
        }
        if (thinkingMethods.length === 0) {
            console.warn('⚠️ [useAiComponentHook] 未配置对应思考方法:', {
                pageId: msg.source.pageId,
                componentId: msg.source.componentId,
                eventHash,
            });
            throw new Error('未配置对应思考方法');
        }
        const rawPayload = msg.eventData.payload;
        const storeIdVal = rawPayload &&
            typeof rawPayload === 'object' &&
            rawPayload.storeId;
        const storeIdStr = typeof storeIdVal === 'string' || typeof storeIdVal === 'number'
            ? String(storeIdVal)
            : '';
        // 数据指纹：payload 实际内容变化时缓存失效，避免命中旧数据结果（如房价建议一直显示 0）
        let dataFingerprint = '';
        if (rawPayload && typeof rawPayload === 'object') {
            const dataPart = rawPayload.data;
            const str = JSON.stringify(dataPart != null ? dataPart : rawPayload);
            dataFingerprint = (await sha256Hex(str)).slice(0, 24);
        }
        const dataKeySuffix = [storeIdStr, dataFingerprint].filter(Boolean).join('_') || undefined;
        const results = [];
        for (let i = 0; i < thinkingMethods.length; i++) {
            const method = thinkingMethods[i];
            // 按流程图：先整理哈希，到缓存表查；含 storeId + 数据指纹，避免不同门店/数据命中同一缓存
            const cacheHash = await generateCacheHash(eventHash, method.method_id, dataKeySuffix);
            let useCached = false;
            if (!forceRefresh) {
                const cacheRes = await fetchFn(`${base(API_PATHS.getCache)}?cacheHash=${encodeURIComponent(cacheHash)}`);
                if (cacheRes.ok) {
                    const cacheBody = await cacheRes.json();
                    // 后端使用 ResponseHelper.success 包装，实际数据在 data 里；兼容未包装的返回
                    const cachePayload = cacheBody?.data ?? cacheBody;
                    const compressedResult = cachePayload?.compressedResult;
                    const blob = compressedResult != null
                        ? typeof compressedResult === 'string'
                            ? (() => {
                                const bin = atob(compressedResult);
                                const arr = new Uint8Array(bin.length);
                                for (let j = 0; j < bin.length; j++)
                                    arr[j] = bin.charCodeAt(j);
                                return new Blob([arr]);
                            })()
                            : compressedResult instanceof Blob
                                ? compressedResult
                                : new Blob([new Uint8Array(compressedResult)])
                        : null;
                    if (blob) {
                        const out = await decompressFromBlob(blob);
                        results.push(out);
                        console.log('✅ [useAiComponentHook] 缓存命中，直接显示:', {
                            methodId: method.method_id,
                            cacheHash,
                        });
                        useCached = true;
                    }
                }
            }
            if (useCached)
                continue;
            // 无缓存或强制刷新：直接从后端获取并写入缓存表
            const questionRaw = (method.detail?.question && String(method.detail.question).trim()) ||
                method.display_name ||
                method.brief_desc ||
                '请根据数据回答问题';
            const affWords = Array.isArray(method.detail?.affirmative_words)
                ? method.detail.affirmative_words
                : [];
            const negWords = Array.isArray(method.detail?.negative_words)
                ? method.detail.negative_words
                : [];
            const thinkingBlock = affWords.length > 0 || negWords.length > 0
                ? `\n\n【思维要求与禁止】\n要求：${affWords.join('，')}\n禁止：${negWords.join('，')}`
                : '';
            const question = questionRaw + thinkingBlock;
            const requiredActions = Array.isArray(method.detail?.required_actions)
                ? method.detail.required_actions
                : [];
            const returnStructureRaw = method.return_format?.format_detail?.structure != null
                ? String(method.return_format.format_detail.structure)
                : '';
            const retAff = Array.isArray(method.return_format?.format_detail?.affirmative_words)
                ? method.return_format.format_detail.affirmative_words
                : [];
            const retNeg = Array.isArray(method.return_format?.format_detail?.negative_words)
                ? method.return_format.format_detail.negative_words
                : [];
            const returnConstraintsBlock = retAff.length > 0 || retNeg.length > 0
                ? `\n\n要求：${retAff.join('，')}\n禁止：${retNeg.join('，')}`
                : '';
            const returnStructure = returnStructureRaw + returnConstraintsBlock;
            // 传实际数据给模型：若 payload 含嵌套的 data（如 current/last7Days/last30Days/last12Months），
            // 则用该 data 作为请求体 data，避免只传结构导致模型得不到真实数据
            // 同时将 payload 顶层的 currentTime/currentHour 等时间字段合并进去，供模型按时间规则判断
            const baseData = rawPayload &&
                typeof rawPayload === 'object' &&
                rawPayload.data != null &&
                typeof rawPayload.data === 'object' &&
                ('current' in rawPayload.data ||
                    'last7Days' in rawPayload.data ||
                    'last30Days' in rawPayload.data ||
                    'last12Months' in rawPayload.data)
                ? rawPayload.data
                : rawPayload;
            const timeFields = rawPayload && typeof rawPayload === 'object'
                ? {
                    currentTime: rawPayload.currentTime,
                    currentTimeLocal: rawPayload.currentTimeLocal,
                    currentHour: rawPayload.currentHour,
                }
                : {};
            const dataForModel = baseData && typeof baseData === 'object'
                ? { ...baseData, ...timeFields }
                : baseData;
            if (process.env.NODE_ENV === 'development') {
                const dataKeys = dataForModel && typeof dataForModel === 'object' ? Object.keys(dataForModel) : [];
                const currentLen = dataForModel &&
                    typeof dataForModel === 'object' &&
                    dataForModel.current != null &&
                    typeof dataForModel.current === 'object' &&
                    Array.isArray(dataForModel.current?.dataSource)
                    ? dataForModel.current.dataSource.length
                    : 0;
                if (dataKeys.length === 0 || currentLen === 0) {
                    console.warn('⚠️ [call-model] 发送的 data 为空或 current 无数据，模型可能无法给出与数据相关的价格:', {
                        dataKeys,
                        currentDataSourceLen: currentLen,
                    });
                }
                console.log('📤 [call-model] 发送的请求体（可在控制台/Network 的 call-model 请求中查看）:', {
                    question,
                    returnStructure: returnStructure || '(无)',
                    dataKeys,
                    dataSummary: dataForModel && typeof dataForModel === 'object'
                        ? Object.fromEntries(Object.entries(dataForModel).map(([k, v]) => [
                            k,
                            v && typeof v === 'object' && 'dataSource' in v
                                ? `dataSource(${Array.isArray(v.dataSource) ? v.dataSource.length : 0}条)`
                                : typeof v === 'object' ? Object.keys(v) : v,
                        ]))
                        : dataForModel,
                    dataFull: dataForModel,
                });
            }
            const modelRes = await fetchFn(base(API_PATHS.callModel), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: msg.context?.token || '',
                },
                body: JSON.stringify({
                    question,
                    requiredActions,
                    returnStructure,
                    data: dataForModel,
                }),
            });
            if (!modelRes.ok) {
                const errText = await modelRes.text();
                throw new Error(`模型调用失败: ${modelRes.status} ${errText}`);
            }
            const modelResult = await modelRes.json();
            // 得到信息后保存到缓存表（流程图：AI 调用 -> ai_result_cache）
            const compressed = await compressToBuffer(modelResult);
            const saveBody = {
                cacheHash,
                eventHash,
                methodId: method.method_id,
                compressedResult: compressed,
                expireDays: 30,
            };
            await fetchFn(base(API_PATHS.saveCache), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: msg.context?.token || '',
                },
                body: JSON.stringify({
                    ...saveBody,
                    compressedResult: Array.from(new Uint8Array(compressed)),
                }),
            });
            results.push(modelResult);
        }
        const byMethodId = {};
        thinkingMethods.forEach((m, idx) => {
            byMethodId[m.method_id] = results[idx];
        });
        return byMethodId;
    }, [
        generateEventHash,
        generateCacheHash,
        decompressFromBlob,
        base,
        fetchFn,
    ]);
    return {
        generateMsgId,
        formatTriggerTime,
        generateEventHash,
        generateCacheHash,
        compressResult,
        decompressResult,
        handleIncomingMessage,
    };
}
